import { all, call, put, race, select, take, takeLatest } from 'redux-saga/effects';

import { NotificationLevel, ProductInstanceStatus } from '@mfe/shared/schema-types';

import { Alert, AlertType, DisplayLocation, setAlerts } from '../alerts';
import { selectUser } from '../auth';
import { selectConfig } from '../config';
import { FetchWithErrorsQuery, graphqlQueryWithErrors } from '../graphql';
import { selectLocale } from '../locale';
import {
  getCharacteristics,
  getPrice,
  getUsage,
  refetchUsage,
  selectCurrentUsage,
  selectPlanCharacteristics,
  selectPlanInfo,
  selectPlanName,
  setCharacteristics,
  setCharacteristicsError,
  setLoadingTier,
  setPrice,
  setPriceError,
  setTier,
  setTierError,
  setUsage,
  setUsageError,
} from './planSlice';
import { GET_CURRENT_USAGE, GET_PLAN_CHARACTERISTICS, GET_PLAN_PRICE, GET_USAGE_TIER } from './query';
import { waitForToken, waitForUserInfo } from '../utils/utilsSagas';

function* fetchPlanCharacteristics() {
  yield call(waitForToken);

  const response: FetchWithErrorsQuery = yield call(graphqlQueryWithErrors, {
    query: GET_PLAN_CHARACTERISTICS,
  });

  const { data, errors, runtimeError } = response;

  if (errors || runtimeError) {
    yield put(setCharacteristicsError(runtimeError ?? errors));
    return;
  }

  if (data?.getPlanCharacteristics) {
    yield put(setCharacteristics(data.getPlanCharacteristics));
  }
}

export function* fetchPlanPrice() {
  yield call(waitForToken);

  const response: FetchWithErrorsQuery = yield call(graphqlQueryWithErrors, {
    query: GET_PLAN_PRICE,
    fetchPolicy: 'network-only',
  });

  const { data, errors, runtimeError } = response;

  if (errors || runtimeError) {
    yield put(setPriceError(runtimeError ?? errors));
    return;
  }

  if (data?.getCurrentPlanPrice) {
    yield put(setPrice(data.getCurrentPlanPrice.price));
  }
}

function* fetchPlanUsage(refetchData = false) {
  yield call(waitForToken);

  const response: FetchWithErrorsQuery = yield call(graphqlQueryWithErrors, {
    query: GET_CURRENT_USAGE,
    fetchPolicy: refetchData ? 'network-only' : 'cache-first',
  });

  const { data, errors, runtimeError } = response;

  if (errors || runtimeError) {
    yield put(setUsageError(runtimeError ?? errors));
    return;
  }

  if (data?.getCurrentUsage) {
    yield put(setUsage(data.getCurrentUsage));
  }
}

export function* refetchUsageData() {
  yield call(fetchPlanUsage, true);
}

export const shouldMockTierDataForUser = (user: { accountNumber: string }, env: string, getMockTier: boolean) => {
  const isMockedAccount = Object.keys(TIER_ACCOUNT_NUMBERS).includes(user.accountNumber);
  return env !== 'PROD' && getMockTier && isMockedAccount;
};

const TIER_ACCOUNT_NUMBERS: Record<string, number> = {
  '5000388509': 0,
  '5000511400': 1,
  '5000510074': 2,
  '5000532034': 0, // Unleashed Max test account tier 0
  '5000533441': 1, // Unleashed Max test account tier 1
  '5000449981': 2, // Unleashed Max test account tier 2
};

const getMockUsageTier = (accountNumber: string) => {
  return TIER_ACCOUNT_NUMBERS[accountNumber];
};

export function* fetchUsageTier() {
  yield put(setLoadingTier());
  yield call(waitForToken);
  yield call(waitForUserInfo);

  const {
    data: {
      characteristics: { isUnleashed, isUnleashedMaxPlan },
    },
  } = yield select(selectPlanInfo);
  const { user } = yield select(selectUser);
  const { enableUnleashedMaxTier } = yield select(selectConfig);

  const { getMockTier, env } = yield select(selectConfig);
  const { accountNumber, productInstanceStatus } = user;

  // If the plan is not part of the Unleashed family (Unleashed / Unleashed Max)
  if (!isUnleashed) {
    return;
  }

  if (productInstanceStatus !== ProductInstanceStatus.Active) {
    yield put(setTierError('The FSI Product Instance is not Active'));
    return;
  }

  if (isUnleashedMaxPlan && !enableUnleashedMaxTier) {
    // Temporarily mock tier if feature is not enabled for Unleashed Max users
    // Remove this once API integration passes tests on production env
    yield put(setTier(0));
    return;
  }

  if (shouldMockTierDataForUser(user, env, getMockTier)) {
    yield put(setTier(getMockUsageTier(accountNumber)));
    return;
  }

  const planName: string = yield select(selectPlanName);

  const response: FetchWithErrorsQuery = yield call(graphqlQueryWithErrors, {
    query: GET_USAGE_TIER,
    variables: { planName },
    fetchPolicy: 'network-only',
  });

  const { data, errors, runtimeError } = response;

  if (errors || runtimeError) {
    yield put(setTierError(runtimeError ?? errors));

    return;
  }

  const tier = data?.getUsageTier?.tier;

  if (typeof tier === 'number' && !isNaN(tier)) {
    yield put(setTier(tier));
  } else {
    yield put(setTierError('Invalid tier'));
  }
}

const getAlertLevel: (receivedAlert: { type?: string }) => NotificationLevel = (receivedAlert) => {
  const level = receivedAlert.type?.toLowerCase();
  if (level && Object.values(NotificationLevel).includes(level as NotificationLevel)) {
    return level as NotificationLevel;
  }
  return NotificationLevel.Info;
};

function* fetchTierAlerts(data: any): Generator<unknown, void, any> {
  const tier = data.payload;
  const { aemContentBaseUrl } = yield select(selectConfig);

  const {
    locale: { userLocale },
  }: ReturnType<typeof selectLocale> = yield select(selectLocale);

  const [languageCode] = userLocale.split('_');
  const MODEL = 'alert';
  const TAG = 'my-alert';
  const AEMURL = `${aemContentBaseUrl}/${MODEL}/${languageCode}/${TAG}.json`;

  if (tier === 2) {
    const response = yield call(fetch, AEMURL);
    const json = yield call([response, 'json']);

    if (json.success) {
      const alerts = Object.keys(json.data.Alerts)
        .filter((key) => key === 'RedTier')
        .map((key) => {
          const receivedAlert = json.data.Alerts[key];
          const alert: Alert = {
            title: receivedAlert.title,
            customDescription: receivedAlert.description,
            level: getAlertLevel(receivedAlert),
            displayLocation: DisplayLocation.GLOBAL,
            type: AlertType.UsageTier,
          };

          if (receivedAlert.ctaLink && receivedAlert.ctaButtonText) {
            alert.button = {
              url: receivedAlert.ctaLink,
              label: receivedAlert.ctaButtonText,
              openInSameTab: true,
            };
          }

          if (receivedAlert.learnMoreLink && receivedAlert.learnMoreText) {
            alert.link = {
              url: receivedAlert.learnMoreLink,
              label: receivedAlert.learnMoreText,
              openInSameTab: true,
            };
          }

          return alert;
        });

      yield put(setAlerts(alerts));
    }
  }
}

export function* waitForPlanCharacteristics() {
  const { loading } = yield select(selectPlanCharacteristics);

  if (loading) {
    yield take(setCharacteristics.type);
  }
}

export function* waitForUsage() {
  const { loading } = yield select(selectCurrentUsage);

  if (loading) {
    yield race({
      setUsageAction: take(setUsage.type),
      setUsageErrorAction: take(setUsageError.type),
    });
  }
}

export function* watchPlan() {
  yield all([
    takeLatest(getCharacteristics.type, fetchPlanCharacteristics),
    takeLatest(setCharacteristics.type, fetchUsageTier),
    takeLatest(getPrice.type, fetchPlanPrice),
    takeLatest(setTier.type, fetchTierAlerts),
    takeLatest(getUsage.type, fetchPlanUsage),
    takeLatest(refetchUsage.type, refetchUsageData),
  ]);
}
